import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { ExportBtn } from "components/common/btns";
import { DEFAULT_PAGINATE_OPTION } from "constants/constants";
import { getSurveyAnswersAPI } from "features/survey/services/RequestApis";
import { Answer } from "features/survey/type";
import { useEffect, useState } from "react";
import { translate } from "utils";
import { formatDateTimeString } from "utils/format";
import { CSVLink } from "react-csv";

interface Props {
	id: string;
}

const AnswersComponent = ({ id }: Props) => {
	const [answers, setAnswers] = useState<Answer[]>([]);
	const [totalQuestions, setTotalQuestions] = useState(0);
	const [paginate, setPaginate] = useState({
		current: 1,
		pageSize: 10,
	});

	useEffect(() => {
		getSurveyAnswersAPI(id).then(data => {
			setAnswers(data.data);
			setTotalQuestions(data.extraData.totalQuestion);
		});
	}, [id]);

	const getQuestionColumns = (): ColumnsType<Answer> => {
		const staticCols: ColumnsType<Answer> = [
			{
				title: translate("Timestamp"),
				dataIndex: "createdAt",
				align: "center",
				render: (_, record) => <p>{formatDateTimeString(record.createdAt)}</p>,
			},
			{
				title: translate("Customer"),
				dataIndex: "author",
				align: "center",
				render: (_, record) => (
					<p>
						{record.type === "customer"
							? record.author?.fullName
							: translate("Guest")}
					</p>
				),
			},
		];

		if (totalQuestions === 0) return staticCols;

		const dynamicCols: ColumnsType<Answer> = Array(totalQuestions)
			.fill("")
			.map((_, index) => {
				return {
					title: translate(`Q`) + (index + 1),
					dataIndex: "",
					align: "center",
					render: (_, record) => <p>{record.answers[index]?.content}</p>,
				};
			});
		return staticCols.concat(dynamicCols);
	};

	const columns: ColumnsType<Answer> = getQuestionColumns();

	const handleChangePage = (page: number) => {
		setPaginate({ ...paginate, current: page });
	};

	const handlePageSizeChange = (_: number, size: number) => {
		setTimeout(() => {
			setPaginate({ current: 1, pageSize: size });
		}, 0);
	};

	const getExportData = () => {
		const data: string[][] = [];
		const titleRow = [translate("Timestamp"), translate("Customer")];
		Array(totalQuestions)
			.fill("")
			.forEach((_, index) => {
				titleRow.push(translate("Q") + (index + 1));
			});
		data.push(titleRow);

		for (let index = 0; index < answers.length; index++) {
			const contentRow: string[] = [];
			const answer = answers[index];
			contentRow.push(formatDateTimeString(answer.createdAt) || "");
			const customer =
				answer.type === "customer"
					? answer.author.fullName
					: translate("Guest");
			contentRow.push(customer);
			for (let i = 0; i < totalQuestions; i++) {
				contentRow.push(answer.answers[i]?.content || "");
			}
			data.push(contentRow);
		}
		return data;
	};

	return (
		<div>
			<div className="w-full flex justify-end mb-4">
				<CSVLink data={getExportData()} filename="answers">
					<ExportBtn />
				</CSVLink>
			</div>

			<div>
				<Table
					dataSource={answers}
					columns={columns}
					bordered
					rowKey={record => record._id}
					scroll={{ x: 1000 }}
					pagination={{
						pageSizeOptions: DEFAULT_PAGINATE_OPTION,
						total: answers.length,
						current: paginate.current,
						pageSize: paginate.pageSize,
						showSizeChanger: true,
						showTotal: total =>
							translate(`Total`) + " " + total + " " + translate(`results`),
						onChange: handleChangePage,
						onShowSizeChange: handlePageSizeChange,
					}}
				/>
			</div>
		</div>
	);
};

export default AnswersComponent;
